import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import apiClient from '../api/client';

const NAV = [
  { label: 'Dashboard', path: '/' },
  { label: 'Properties', path: '/properties' },
  { label: 'Clients', path: '/clients' },
  { label: 'Schedule', path: '/schedule' },
  { label: 'Map', path: '/map' },
  { label: 'Plan Day', path: '/plan' },
  { label: 'Today', path: '/today' },
];

const Header = ({ user, onLogin, onLogout }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [roles, setRoles] = useState(null);
  const [loadingRoles, setLoadingRoles] = useState(false);

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  const openSwitch = async (e) => {
    setAnchorEl(e.currentTarget);
    if (!roles) {
      setLoadingRoles(true);
      try {
        const allRoles = await apiClient.get('/auth/roles');
        setRoles(allRoles.data.roles);
      } catch {
        setRoles([]);
      } finally {
        setLoadingRoles(false);
      }
    }
  };

  const assume = async (org_user_id) => {
    setAnchorEl(null);
    try {
      const { data } = await apiClient.post('/auth/assume', { org_user_id });
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      onLogin(data.user);
    } catch {}
  };

  const allNav = [
    ...NAV,
    ...(user?.org_id ? [{ label: 'My Org', path: '/org' }] : []),
  ];

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: isMobile ? 1 : 0, mr: isMobile ? 0 : 4 }}>
            Showing Planner
          </Typography>

          {/* Desktop nav */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1, flexGrow: 1 }}>
              {allNav.map(({ label, path }) => (
                <Button
                  key={path}
                  component={Link}
                  to={path}
                  color="inherit"
                  variant={pathname === path ? 'outlined' : 'text'}
                >
                  {label}
                </Button>
              ))}
            </Box>
          )}

          {/* Desktop user info */}
          {!isMobile && user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Chip
                label={user.org_name ? `${user.org_name} · ${user.org_role}` : 'No context'}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'inherit', cursor: 'pointer' }}
                onClick={openSwitch}
              />
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                {user.first_name} {user.last_name}
              </Typography>
              <Button color="inherit" variant="outlined" size="small" onClick={handleLogout}>
                Logout
              </Button>
            </Box>
          )}

          {/* Mobile hamburger */}
          {isMobile && (
            <IconButton color="inherit" onClick={() => setDrawerOpen(true)}>
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 240, pt: 1 }}>
          {user && (
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="body2" fontWeight={600}>{user.first_name} {user.last_name}</Typography>
              {user.org_name && (
                <Typography variant="caption" color="text.secondary">{user.org_name} · {user.org_role}</Typography>
              )}
            </Box>
          )}
          <Divider />
          <List disablePadding>
            {allNav.map(({ label, path }) => (
              <ListItemButton
                key={path}
                component={Link}
                to={path}
                selected={pathname === path}
                onClick={() => setDrawerOpen(false)}
              >
                <ListItemText primary={label} />
              </ListItemButton>
            ))}
          </List>
          <Divider />
          {user && (
            <List disablePadding>
              <ListItemButton onClick={openSwitch}>
                <ListItemText primary="Switch brokerage" />
              </ListItemButton>
              <ListItemButton onClick={() => { setDrawerOpen(false); handleLogout(); }}>
                <ListItemText primary="Logout" />
              </ListItemButton>
            </List>
          )}
        </Box>
      </Drawer>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <Typography variant="caption" sx={{ px: 2, py: 1, color: 'text.secondary', display: 'block' }}>
          Switch context
        </Typography>
        <Divider />
        {loadingRoles && <MenuItem disabled>Loading…</MenuItem>}
        {roles?.map(r => (
          <MenuItem
            key={r.id}
            onClick={() => assume(r.id)}
            selected={r.id === user?.org_user_id}
          >
            {r.org_name}
            {r.is_personal && <Chip label="personal" size="small" sx={{ ml: 1 }} />}
            <Chip label={r.role} size="small" sx={{ ml: 1 }} />
          </MenuItem>
        ))}
        {user?.is_system_admin && [
          <Divider key="div" />,
          <MenuItem
            key="admin"
            component="a"
            href="/admin"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setAnchorEl(null)}
          >
            Admin Portal ↗
          </MenuItem>,
        ]}
      </Menu>
    </>
  );
};

export default Header;
